import Launch20 from '@carbon/icons-react/es/launch/20';
import { Space, Tooltip, Typography } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import styled, { css } from 'styled-components';
import { Iframely as IframelyAtom } from 'components/atoms/iframely';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import { FilterBar } from 'components/molecules/filter-bar';
import { FilterBarSecondary } from 'components/organisms/spaces/space-tab/styles';
import { SpaceTab } from 'data/space-tabs';
import {
  selectSpaceName,
  selectSpaceTabName,
} from 'data/templates/projects/selectors';
import { DocumentTemplateMoreActions } from './more-actions';

const IFrameHeight = css`
  height: 570px !important;
`;
const Iframely = styled(IframelyAtom)`
  height: 90%;
  overflow: auto;

  iframe {
    ${IFrameHeight};
  }
`;

const Iframe = styled.iframe`
  ${IFrameHeight};
  width: 100%;
`;

const StyledText = styled(Typography.Text)`
  max-width: 400px;
`;

const TertiaryButtonStyled = styled(TertiaryButton)`
  border: none;
  color: inherit;

  svg {
    width: var(--spacing-05);
    height: var(--spacing-05);
  }
`;

export const TemplateSpacesEmbeddedDocument: FC<{
  data: SpaceTab;
  spaceId: number;
}> = ({ data, spaceId }) => {
  const site = data.metaData?.meta?.site;
  const spaceName = useSelector(selectSpaceName(spaceId));
  const { pathname } = useLocation();
  const spaceTabId = Number(pathname.split('/').pop());
  const spaceTabName = useSelector(selectSpaceTabName(spaceId, spaceTabId));

  return (
    <>
      <FilterBar>
        <FilterBar.Primary>
          <div style={{ whiteSpace: 'nowrap' }}>
            <StyledText ellipsis={{ tooltip: true }}>{spaceName}</StyledText>
            <span>&nbsp;/&nbsp;</span>
            <StyledText ellipsis={{ tooltip: true }}>{spaceTabName}</StyledText>
          </div>
        </FilterBar.Primary>
        <FilterBarSecondary>
          <Space size={8} align="center">
            <Tooltip
              title={
                <FormattedMessage
                  id={site ? 'space_tab.open_in' : 'space_tab.open_in.new_tab'}
                  values={site ? { name: site } : {}}
                />
              }
              placement="right"
            >
              <TertiaryButtonStyled
                href={data.url}
                target="_blank"
                icon={<Launch20 />}
                size="small"
              />
            </Tooltip>

            <DocumentTemplateMoreActions
              data={data}
              spaceId={spaceId}
              canShowEdit
            />
          </Space>
          <Space size={8} align="center" />
        </FilterBarSecondary>
      </FilterBar>
      {/* Note: We couldn't find why calendly is not working, so we are directly loading it in iframe. */}
      {data.metaData?.url?.includes('calendly') || !data.metaData?.html ? (
        <Iframe title={data.spaceTabTitle} src={data.url} />
      ) : (
        <Iframely html={data.metaData.html} />
      )}
    </>
  );
};
